import type { FormConfig } from '../../config/types';
import { type ExcelValueConflict, mergeExcelValues } from '../excel-import-conflicts';
import { type CdrSection, readCdrWorkbook } from './workbook';

export type CdrConflict = ExcelValueConflict & { sectionId: string; field: string };

export const readCdrWorkbooks = async (files: File[], config: FormConfig) => {
  const sources: { source: string; sections: CdrSection[] }[] = [];
  for (const file of files) {
    try {
      const sections = await readCdrWorkbook(file, config);
      if (!sections) throw new Error('Select a 2025-26 or 2026-27 CDR workbook.');
      sources.push({ source: file.name, sections });
    } catch (error) {
      throw new Error(
        `${file.name}: ${error instanceof Error ? error.message : 'The workbook could not be read.'}`,
      );
    }
  }
  const conflicts: CdrConflict[] = [];
  const sections = (sources[0]?.sections || []).map((section): CdrSection => {
    const matching = sources.map((source) => ({
      source: source.source,
      section: source.sections.find((item) => item.id === section.id)!,
    }));
    const merged = mergeExcelValues(
      matching.map(({ source, section }) => ({ source, values: section.values })),
      (field) =>
        `${section.title}: ${section.page.questions?.find((question) => question.name === field)?.title || field}`,
    );
    conflicts.push(
      ...merged.conflicts.map((conflict) => ({
        ...conflict,
        key: `${section.id}:${conflict.key}`,
        field: conflict.key,
        sectionId: section.id,
      })),
    );
    return {
      ...section,
      rows: matching.flatMap(({ section }) => section.rows),
      values: merged.values,
      notes: [...new Set(matching.flatMap(({ section }) => section.notes))],
      sourceErrors: matching.flatMap(({ source, section }) =>
        (section.sourceErrors || []).map((error) => `${source}: ${error}`),
      ),
    };
  });
  return { sections, conflicts };
};
